import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Products from './components/Products';
import Orders from './components/Orders';
import Offers from './components/Offers';
import Suppliers from './components/Suppliers';
import Staff from './components/Staff';
import Notifications from './components/Notifications';
import Inventory from './components/Inventory';
import Customers from './components/Customers';
import Reviews from './components/Reviews';
import Pincode from './components/Pincode';
import Banners from './components/Banners';
import Promotions from './components/Promotions';
import BestProductsAdmin from './components/BestProducts';
import AboutMissionAdmin from './components/AboutMission';
// import Expenses from './components/Expenses';
import Analytics from './components/Analytics';
import Settings from './components/Settings';
import Login from './components/Login';
import ProductModal from './components/ProductModal';
import CategoryModal from './components/CategoryModal';
import StaffModal from './components/StaffModal';
import SupplierModal from './components/SupplierModal';
import ToastContainer from './components/Toast';

import Expenses from './components/Expenses/index';
import {
  fetchProducts,
  fetchCategories,
  createProduct as createProductApi,
  updateProduct as updateProductApi,
  deleteProduct as deleteProductApi,
  createCategory as createCategoryApi,
  fetchCombos,
  createCombo as createComboApi,
  updateCombo as updateComboApi,
  deleteCombo as deleteComboApi,
  fetchVendors,
  createVendor as createVendorApi,
  updateVendor as updateVendorApi,
  deleteVendor as deleteVendorApi,
  toggleVendorStatus as toggleVendorStatusApi,
  fetchStaff,
  createStaff as createStaffApi,
  updateStaff as updateStaffApi,
  deleteStaff as deleteStaffApi,
  toggleStaffStatus as toggleStaffStatusApi,
  fetchOrders,
} from './api';


const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // ── Persist login across page refreshes ────────────────────────────────────
  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('adminUser') || '{}'); } catch { return {}; }
  })();
  const hasToken = !!localStorage.getItem('adminToken');

  const [isLoggedIn, setIsLoggedIn] = useState(hasToken);
  const [userRole, setUserRole] = useState(storedUser?.role || (hasToken ? 'super_admin' : ''));
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showCategoryFromProduct, setShowCategoryFromProduct] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [combos, setCombos] = useState([]);
  const [isLoadingCombos, setIsLoadingCombos] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [staff, setStaff] = useState([]);
  const [isLoadingStaff, setIsLoadingStaff] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [toasts, setToasts] = useState([]);

  const showToast = (type, title, message = '') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, title, message }]);
  };
  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const handleLogin = (role) => {
    setIsLoggedIn(true);
    setUserRole(role);
    const nextTab = role === 'super_admin' ? 'dashboard' : 'products';
    navigate(`/${nextTab}`, { replace: true });
  };

  const handleLogout = () => {
    // Clear auth data from storage
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsLoggedIn(false);
    setUserRole('');
    navigate('/', { replace: true });
  };

  // ── Initial data load ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) return;

    const loadInitialData = async () => {
      try {
        setIsLoadingProducts(true);
        setIsLoadingCombos(true);

        setIsLoadingSuppliers(true);
        setIsLoadingStaff(true);
        const [productsFromApi, categoriesFromApi, combosFromApi, vendorsFromApi, staffFromApi] = await Promise.all([
          fetchProducts().catch((err) => {
            console.error('Failed to load products', err);
            return [];
          }),
          fetchCategories().catch((err) => {
            console.error('Failed to load categories', err);
            return [];
          }),
          fetchCombos().catch((err) => {
            console.error('Failed to load combos', err);
            return [];
          }),
          fetchVendors().catch((err) => {
            console.error('Failed to load vendors', err);
            return [];
          }),
          fetchStaff().catch((err) => {
            console.error('Failed to load staff', err);
            return [];
          }),
        ]);

        setProducts(productsFromApi || []);
        setCategories(categoriesFromApi || []);
        setCombos(combosFromApi || []);
        setSuppliers(vendorsFromApi || []);
        setStaff(staffFromApi || []);
      } finally {
        setIsLoadingProducts(false);
        setIsLoadingCombos(false);
        setIsLoadingSuppliers(false);
        setIsLoadingStaff(false);
      }
    };

    loadInitialData();
  }, [isLoggedIn]);

  // ── Global order notifications → toast ──────────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn || userRole !== 'super_admin') return;

    const SEEN_NOTIF_KEY = 'seenOrderNotificationIds';
    const SEEN_ORDERS_KEY = 'seenOrderStatusMap';

    const seenNotif = new Set();
    try {
      const saved = JSON.parse(localStorage.getItem(SEEN_NOTIF_KEY) || '[]');
      if (Array.isArray(saved)) saved.forEach((id) => seenNotif.add(String(id)));
    } catch (_) {}

    let lastOrderStatus = {};
    try {
      const saved = JSON.parse(localStorage.getItem(SEEN_ORDERS_KEY) || '{}');
      if (saved && typeof saved === 'object') lastOrderStatus = saved;
    } catch (_) {}

    let cancelled = false;

    const persistNotifs = () => {
      try {
        localStorage.setItem(SEEN_NOTIF_KEY, JSON.stringify(Array.from(seenNotif).slice(-500)));
      } catch (_) {}
    };

    const persistOrders = () => {
      try {
        localStorage.setItem(SEEN_ORDERS_KEY, JSON.stringify(lastOrderStatus));
      } catch (_) {}
    };

    const buildItemsSummary = (items) => {
      const list = Array.isArray(items) ? items : [];
      if (!list.length) return '';
      const parts = list
        .map((it) => {
          const name = (it?.item_name ?? it?.itemName ?? it?.product_name ?? it?.productName ?? '').toString().trim();
          if (!name) return null;
          const qty = Number(it?.quantity ?? 1);
          return qty > 1 ? `${name}×${qty}` : name;
        })
        .filter(Boolean);
      if (!parts.length) return '';
      const max = 3;
      const shown = parts.slice(0, max);
      const remaining = parts.length - shown.length;
      return remaining > 0 ? `${shown.join(', ')} +${remaining}` : shown.join(', ');
    };

    const toExactDateTime = (iso) => {
      const d = iso ? new Date(iso) : null;
      if (!d || Number.isNaN(d.getTime())) return '—';
      const date = d.toLocaleDateString('en-GB').replace(/\//g, '.');
      const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      return `${date} ${time}`;
    };

    const toastFor = ({ status, orderId, customerName, itemsSummary, createdAt }) => {
      const isCancel = String(status || '').toLowerCase() === 'cancelled';
      const title = isCancel ? 'Order Cancelled' : 'New Order';
      const msgParts = [
        `#${orderId}${customerName ? ` • ${customerName}` : ''}`,
        itemsSummary ? `Items: ${itemsSummary}` : null,
        createdAt ? `At: ${toExactDateTime(createdAt)}` : null,
      ].filter(Boolean);
      showToast(isCancel ? 'error' : 'success', title, msgParts.join(' | '));
    };

    const poll = async (showToasts) => {
      try {
        // Poll orders and detect new/cancelled
        const orders = await fetchOrders().catch((err) => {
          console.error('Toast notifications: failed to fetch orders', err);
          return [];
        });
        if (cancelled) return;
        const nextStatus = { ...lastOrderStatus };

        const newEvents = [];
        (orders || []).forEach((o) => {
          const id = String(o.id ?? o.order_id ?? '');
          if (!id) return;
          const status = String(o.status ?? '').toLowerCase();
          const customer = o.customer_name ?? o.customerName ?? o.customer ?? '';
          const createdAt = o.updated_at ?? o.order_date ?? o.created_at ?? null;
          const itemsSummary = buildItemsSummary(o.items);

          if (!lastOrderStatus[id]) {
            nextStatus[id] = status || 'pending';
            newEvents.push({ id: `new-${id}`, status, orderId: id, customerName: customer, createdAt, itemsSummary });
            return;
          }

          if (status === 'cancelled' && lastOrderStatus[id] !== 'cancelled') {
            nextStatus[id] = 'cancelled';
            newEvents.push({ id: `cancel-${id}`, status, orderId: id, customerName: customer, createdAt, itemsSummary });
            return;
          }

          nextStatus[id] = status || lastOrderStatus[id];
        });

        lastOrderStatus = nextStatus;
        persistOrders();

        if (showToasts && newEvents.length) {
          console.log('Toast notifications: new order events', newEvents);
          newEvents.slice(0, 3).forEach((e) => {
            if (seenNotif.has(e.id)) return;
            seenNotif.add(e.id);
            toastFor(e);
          });
          persistNotifs();
        }
      } catch (e) {
        console.error('Toast notifications: polling failed', e);
      }
    };

    // seed without toasts (baseline), then start polling
    poll(false);
    const t = setInterval(() => poll(true), 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [isLoggedIn, userRole]);

  const openModal = (type, entity = null) => {
    setModalType(type);
    if (type === 'product') setEditingProduct(entity);
    else if (type === 'supplier') setEditingSupplier(entity);
    else if (type === 'staff') setEditingStaff(entity);
    else setEditingProduct(entity);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setEditingProduct(null);
    setEditingSupplier(null);
    setEditingStaff(null);
    setShowCategoryFromProduct(false);
  };

  const handleAddCategoryFromProduct = () => {
    setShowCategoryFromProduct(true);
  };

  // ── Product CRUD handlers (connected to API) ────────────────────────────────
  const handleCreateProduct = async (productData, images) => {
    try {
      const created = await createProductApi({ ...productData, images });
      if (created) {
        setProducts((prev) => [...prev, created]);
        showToast('success', 'Product added', `${productData.name || 'Product'} has been added to your catalog.`);
      }
      closeModal();
    } catch (error) {
      console.error('Error creating product', error);
      showToast('error', 'Could not add product', error.response?.data?.message || error.message || 'Please try again.');
    }
  };

  const handleUpdateProduct = async (productData, images) => {
    try {
      const id = productData.id || editingProduct?.id;
      if (!id) {
        console.error('Missing product id for update');
        return;
      }
      const updated = await updateProductApi(id, { ...productData, images });
      // Build display shape from submitted form so the row updates even if API returns minimal
      const editedRow = {
        id: editingProduct?.id ?? id,
        name: productData.name,
        category: productData.category ?? editingProduct?.category,
        categoryId: productData.categoryId,
        price: Number(productData.price),
        castingPrice: Number(productData.castingPrice),
        stock: Number(productData.stock),
        stockUnit: productData.stockUnit ?? editingProduct?.stockUnit,
        weight: productData.weight != null ? Number(productData.weight) : editingProduct?.weight,
        expiryDate: productData.expiryDate ?? editingProduct?.expiryDate,
        batchNumber: productData.batchNumber ?? editingProduct?.batchNumber,
        imageUrl: editingProduct?.imageUrl,
      };
      setProducts((prev) =>
        prev.map((p) =>
          String(p.id) === String(id)
            ? { ...p, ...updated, ...editedRow, id: p.id }
            : p
        )
      );
      showToast('success', 'Product updated', `${productData.name || 'Product'} has been updated successfully.`);
      closeModal();
    } catch (error) {
      console.error('Error updating product', error);
      showToast('error', 'Could not update product', error.response?.data?.message || error.message || 'Please try again.');
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await deleteProductApi(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      showToast('success', 'Product removed', 'The product has been deleted from your catalog.');
    } catch (error) {
      console.error('Error deleting product', error);
      showToast('error', 'Could not delete product', error.response?.data?.message || error.message || 'Please try again.');
    }
  };

  // ── Category handlers ───────────────────────────────────────────────────────
  const addCategory = async (categoryData) => {
    try {
      const created = await createCategoryApi(categoryData);
      if (created) {
        setCategories((prev) => [...prev, created]);
      }
      if (showCategoryFromProduct) {
        setShowCategoryFromProduct(false);
      } else {
        closeModal();
      }
    } catch (error) {
      console.error('Error creating category', error);
    }
  };

  const addStaff = async (staffData) => {
    try {
      const id = editingStaff?.id;
      if (id != null) {
        const updated = await updateStaffApi(id, staffData);
        const editedRow = {
          id,
          name: staffData.name ?? editingStaff?.name,
          employeeId: staffData.employeeId ?? editingStaff?.employeeId,
          phone: staffData.phone ?? editingStaff?.phone,
          email: staffData.email ?? editingStaff?.email,
          designation: staffData.designation ?? editingStaff?.designation,
          position: staffData.designation ?? editingStaff?.position,
          status: editingStaff?.status,
          createdAt: editingStaff?.createdAt,
          updatedAt: editingStaff?.updatedAt,
        };
        setStaff((prev) =>
          prev.map((s) => (String(s.id) === String(id) ? { ...s, ...updated, ...editedRow, id: s.id } : s))
        );
        showToast('success', 'Staff updated', `${staffData.name} has been updated.`);
      } else {
        const created = await createStaffApi(staffData);
        const newRow = {
          ...created,
          id: created?.id ?? `new-${Date.now()}`,
          name: staffData.name ?? created?.name,
          employeeId: staffData.employeeId ?? created?.employeeId,
          phone: staffData.phone ?? created?.phone,
          email: staffData.email ?? created?.email,
          designation: staffData.designation ?? created?.designation,
          position: staffData.designation ?? created?.position,
          status: created?.status ?? 'active',
        };
        setStaff((prev) => [...prev, newRow]);
        showToast('success', 'Staff added', `${staffData.name} has been added.`);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving staff', err);
      showToast('error', 'Could not save staff', err.response?.data?.message || err.message || 'Please try again.');
    }
  };

  const handleDeleteStaff = async (id) => {
    try {
      await deleteStaffApi(id);
      setStaff((prev) => prev.filter((s) => s.id !== id));
      showToast('success', 'Staff removed', 'The staff member has been removed.');
    } catch (err) {
      console.error('Error deleting staff', err);
      showToast('error', 'Could not delete staff', err.response?.data?.message || err.message || 'Please try again.');
    }
  };

  const handleToggleStaffStatus = async (id) => {
    try {
      const updated = await toggleStaffStatusApi(id);
      if (updated) {
        setStaff((prev) => prev.map((s) => (String(s.id) === String(updated.id) ? updated : s)));
        showToast('success', 'Status updated', `Staff is now ${updated.status}.`);
      }
    } catch (err) {
      console.error('Error toggling staff status', err);
      showToast('error', 'Could not update status', err.response?.data?.message || err.message || 'Please try again.');
    }
  };

  const addSupplier = async (supplierData) => {
    try {
      const id = editingSupplier?.id;
      if (id != null) {
        const updated = await updateVendorApi(id, supplierData);
        const editedRow = {
          id,
          name: supplierData.name ?? editingSupplier?.name,
          contact: supplierData.contact ?? editingSupplier?.contact,
          phone: supplierData.contact ?? editingSupplier?.phone,
          email: supplierData.email ?? editingSupplier?.email,
          address: supplierData.address ?? editingSupplier?.address,
          gstNumber: supplierData.gstNumber ?? editingSupplier?.gstNumber,
          status: editingSupplier?.status,
          createdAt: editingSupplier?.createdAt,
          updatedAt: editingSupplier?.updatedAt,
        };
        setSuppliers((prev) =>
          prev.map((s) => (String(s.id) === String(id) ? { ...s, ...updated, ...editedRow, id: s.id } : s))
        );
        showToast('success', 'Supplier updated', `${supplierData.name} has been updated.`);
      } else {
        const created = await createVendorApi(supplierData);
        const newRow = {
          ...created,
          id: created?.id ?? `new-${Date.now()}`,
          name: supplierData.name ?? created?.name,
          contact: supplierData.contact ?? created?.contact ?? created?.phone,
          phone: supplierData.contact ?? created?.phone ?? created?.contact,
          email: supplierData.email ?? created?.email,
          address: supplierData.address ?? created?.address,
          gstNumber: supplierData.gstNumber ?? created?.gstNumber,
          status: created?.status ?? 'active',
        };
        setSuppliers((prev) => [...prev, newRow]);
        showToast('success', 'Supplier added', `${supplierData.name} has been added.`);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving supplier', err);
      showToast('error', 'Could not save supplier', err.response?.data?.message || err.message || 'Please try again.');
    }
  };

  const handleDeleteSupplier = async (id) => {
    try {
      await deleteVendorApi(id);
      setSuppliers((prev) => prev.filter((s) => s.id !== id));
      showToast('success', 'Supplier removed', 'The supplier has been deleted.');
    } catch (err) {
      console.error('Error deleting supplier', err);
      showToast('error', 'Could not delete supplier', err.response?.data?.message || err.message || 'Please try again.');
    }
  };

  const handleToggleSupplierStatus = async (id) => {
    try {
      const updated = await toggleVendorStatusApi(id);
      if (updated) {
        setSuppliers((prev) => prev.map((s) => (String(s.id) === String(updated.id) ? updated : s)));
        showToast('success', 'Status updated', `Supplier is now ${updated.status}.`);
      }
    } catch (err) {
      console.error('Error toggling supplier status', err);
      showToast('error', 'Could not update status', err.response?.data?.message || err.message || 'Please try again.');
    }
  };

  // ── Combo (Offers) handlers ─────────────────────────────────────────────────
  const handleCreateCombo = async (comboData) => {
    try {
      const created = await createComboApi(comboData);
      if (created) {
        setCombos((prev) => [...prev, created]);
      }
    } catch (error) {
      console.error('Error creating combo', error);
    }
  };

  const handleUpdateCombo = async (id, comboData) => {
    try {
      const updated = await updateComboApi(id, comboData);
      if (updated) {
        setCombos((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c))
        );
      }
    } catch (error) {
      console.error('Error updating combo', error);
    }
  };

  const handleDeleteCombo = async (id) => {
    try {
      await deleteComboApi(id);
      setCombos((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error('Error deleting combo', error);
    }
  };

  const currentTab = (() => {
    const segment = location.pathname.split('/')[1] || '';
    if (!segment) {
      return userRole === 'super_admin' ? 'dashboard' : 'products';
    }
    return segment;
  })();

  const renderContent = () => {
    const stats = {
      totalProducts: products.length,
      totalOrders: 128,
      totalRevenue: 45000,
      totalCustomers: 89
    };

    const notifications = [
      { id: 1, message: 'New order received from John Doe', time: '2 minutes ago', type: 'order' },
      { id: 2, message: 'Low stock alert for Organic Tomatoes', time: '1 hour ago', type: 'stock' },
      { id: 3, message: 'Payment received for Order #1234', time: '3 hours ago', type: 'payment' }
    ];

    const orders = [
      { id: 1, customerName: 'John Doe', total: 1250, date: '20.01.2024', status: 'completed' },
      { id: 2, customerName: 'Jane Smith', total: 890, date: '21.01.2024', status: 'pending' }
    ];

    switch (currentTab) {
      case 'dashboard':
        return userRole === 'super_admin'
          ? (
            <Dashboard
              stats={stats}
              notifications={notifications}
              onNotificationClick={() => navigate('/notifications')}
              orders={orders}
              products={products}
            />
          )
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'products':
        return (
          <Products
            products={products}
            loading={isLoadingProducts}
            onAdd={() => openModal('product')}
            onEdit={(product) => openModal('product', product)}
            onDelete={handleDeleteProduct}
          />
        );
      case 'inventory':
        return userRole === 'super_admin'
          ? <Inventory products={products} categories={categories} />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'orders':
        return <Orders />;
      case 'customers':
        return userRole === 'super_admin'
          ? <Customers />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'reviews':
        return userRole === 'super_admin'
          ? <Reviews />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'banners':
        return userRole === 'super_admin'
          ? <Banners />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'promotions':
        return userRole === 'super_admin'
          ? <Promotions />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'best-products':
        return userRole === 'super_admin'
          ? <BestProductsAdmin />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'about-mission':
        return userRole === 'super_admin'
          ? <AboutMissionAdmin />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'pincode':
        return userRole === 'super_admin'
          ? <Pincode />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'offers':
        return (
          <Offers
            offers={combos}
            loading={isLoadingCombos}
            onAdd={handleCreateCombo}
            onEdit={handleUpdateCombo}
            onDelete={handleDeleteCombo}
            products={products}
          />
        );
      case 'suppliers':
        return userRole === 'super_admin'
          ? (
            <Suppliers
              suppliers={suppliers}
              products={products}
              loading={isLoadingSuppliers}
              onAddSupplier={() => openModal('supplier')}
              onEditSupplier={(s) => openModal('supplier', s)}
              onDeleteSupplier={handleDeleteSupplier}
              onToggleStatus={handleToggleSupplierStatus}
              totalProductsCount={products.length}
            />
          )
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'staff':
        return userRole === 'super_admin'
          ? (
            <Staff
              staff={staff}
              loading={isLoadingStaff}
              orders={orders}
              onAddStaff={() => openModal('staff')}
              onEditStaff={(s) => openModal('staff', s)}
              onDeleteStaff={handleDeleteStaff}
              onToggleStatus={handleToggleStaffStatus}
            />
          )
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'expenses':
        return userRole === 'super_admin' ? < Expenses /> : <Login onLogin={handleLogin} />;
      case 'analytics':
        return userRole === 'super_admin'
          ? <Analytics products={products} orders={orders} />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'settings':
        return userRole === 'super_admin'
          ? <Settings />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      case 'notifications':
        return userRole === 'super_admin'
          ? <Notifications />
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
      default:
        return userRole === 'super_admin'
          ? (
            <Dashboard
              stats={stats}
              notifications={notifications}
              onNotificationClick={() => navigate('/notifications')}
              orders={orders}
              products={products}
            />
          )
          : (
            <Products
              products={products}
              loading={isLoadingProducts}
              onAdd={() => openModal('product')}
              onEdit={(product) => openModal('product', product)}
              onDelete={handleDeleteProduct}
            />
          );
    }
  };  

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen">
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      <Sidebar
        activeTab={currentTab}
        onNavigate={(tabId) => navigate(`/${tabId}`)}
        onLogout={handleLogout}
        userRole={userRole}
      />
      <main className="ml-[280px] min-h-screen h-screen overflow-y-auto ui-scrollbar bg-[#F3F1EB]">
        {renderContent()}
      </main>

      {showModal && (
        <>
          {modalType === 'product' && (
            <ProductModal
              categories={categories}
              suppliers={suppliers}
              onClose={closeModal}
              onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}
              editingProduct={editingProduct}
              onAddCategory={handleAddCategoryFromProduct}
            />
          )}
          {modalType === 'staff' && (
            <StaffModal
              isOpen={showModal}
              onClose={closeModal}
              onSubmit={addStaff}
              editingStaff={editingStaff}
            />
          )}
          {modalType === 'supplier' && (
            <SupplierModal
              isOpen={showModal}
              onClose={closeModal}
              onSubmit={addSupplier}
              editingSupplier={editingSupplier}
            />
          )}
          {(modalType === 'category' || showCategoryFromProduct) && (
            <CategoryModal
              isOpen={showModal || showCategoryFromProduct}
              onClose={() => {
                if (showCategoryFromProduct) {
                  setShowCategoryFromProduct(false);
                } else {
                  closeModal();
                }
              }}
              onSubmit={addCategory}
            />
          )}
        </>
      )}
    </div>
  );
};

export default App;